import React, { useState, useEffect } from 'react';
import { formatInTenantTz } from '../../lib/datetime';
import { isMeaningfulResolution } from '../../lib/solutionResolution';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useBusiness } from '../../contexts/BusinessContext';
import { useTenantCategories } from './SolutionBank/useTenantCategories';
import { useAuth } from '../../contexts/AuthContext';
import { useCRMAuth } from '../../contexts/CRMAuthContext';
import { normalizeBusinessRole } from '../../lib/role';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../hooks/use-toast';
import SolutionBankReview from './SolutionBank/SolutionBankReview';
import KnowledgeBuilder from './SolutionBank/KnowledgeBuilder';
import LiveAiKnowledgePanel from './SolutionBank/LiveAiKnowledgePanel';
import {
  Search,
  Database,
  Plus,
  TrendingUp,
  FileText,
  Edit,
  Trash2,
  Filter,
  Download,
  Upload,
  BookOpen,
  Target,
  Loader2,
  BarChart3,
  Lightbulb,
  Sparkles,
  Activity
} from 'lucide-react';

const SolutionBank = () => {
  const { t } = useLanguage();
  // Business-role gate for the manager/admin-only REVIEW queue. Canonical business-role source =
  // useAuth().user (same as BusinessSidebar / EscalationManager / SolutionBankReview). An Operator must
  // NOT see the Review tab nor trigger the approver-only GET ...?status=pending_approval (backend 403).
  const { user } = useAuth();
  const canReview = ['admin', 'manager'].includes(normalizeBusinessRole(user?.role));
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addSolutionOpen, setAddSolutionOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [importCsvOpen, setImportCsvOpen] = useState(false);
  const [exportAllOpen, setExportAllOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [editSolutionOpen, setEditSolutionOpen] = useState(false);
  const [deleteSolutionOpen, setDeleteSolutionOpen] = useState(false);
  const [solutionToEdit, setSolutionToEdit] = useState(null);
  const [solutionToDelete, setSolutionToDelete] = useState(null);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [newSolution, setNewSolution] = useState({
    title: '',
    description: '',
    // No default category. A hardcoded default is the static authority wearing a different hat: it
    // silently ships a category the tenant never defined whenever the author does not open the picker.
    category: '',
    tags: '',
    resolution: '',
    difficulty: 'Medium'
  });
  const { 
    // `currentBusiness` is the active Business tenant this whole context already scopes every call
    // to (getSolutions, analytics, staff all read currentBusiness.brandId). It was ALWAYS exported
    // here; the category work referenced it without destructuring it, which is what crashed the
    // route with "currentBusiness is not defined".
    currentBusiness,
    getSolutions, 
    createSolution, 
    updateSolution, 
    deleteSolution, 
    importSolutions, 
    exportSolutions, 
    generatePerformanceReport 
  } = useBusiness();
  // The authenticated fetch every other Business component uses (useCRMAuth), not a local one.
  const { makeAuthenticatedRequest } = useCRMAuth();
  const { toast } = useToast();

  // The tenant's effective categories — the ONE authority (Category Management -> TaxonomyService),
  // the same set the review modal and the backend approval gate resolve. The five-value array that
  // used to live here was a second authority: none of its values existed in any tenant's taxonomy, so
  // everything authored through these dialogs was rejected 422 at approval.
  const { categories: tenantCategories, loaded: categoriesLoaded } =
    useTenantCategories(currentBusiness?.brandId, makeAuthenticatedRequest);
  // 'all' is a FILTER sentinel only. It is never a category and is never submitted as one.
  const categories = ['all', ...tenantCategories];
  const canAuthorCategory = tenantCategories.length > 0;

  // Display-only label for a category value. The stored value stays English (used as the API filter
  // and persisted category); only the visible label is localized. 'all' is a UI-only sentinel.
  // Only the UI sentinel is translatable. A tenant-authored category name is content, not copy: the
  // legacy entries here would have quietly relabelled a tenant's own "Billing" in another language,
  // showing something the approval gate never sees. Tenant names render verbatim.
  const CATEGORY_LABEL_KEY = {
    'all': 'sb.allCategories',
  };
  const categoryLabel = (category) => (CATEGORY_LABEL_KEY[category] ? t(CATEGORY_LABEL_KEY[category]) : category);

  // Load solutions on component mount and category change
  useEffect(() => {
    loadSolutions();
  }, [selectedCategory]);

  const loadSolutions = async () => {
    try {
      setLoading(true);
      const solutionsData = await getSolutions(selectedCategory);
      setSolutions(solutionsData);
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('sb.toast.loadFailed'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddSolution = async () => {
    if (!newSolution.title || !newSolution.description) {
      toast({
        title: t('sb.toast.missingInfo'),
        description: t('sb.toast.missingInfoDesc'),
        variant: "destructive",
      });
      return;
    }
    // SLICE A — a Solution Bank candidate must carry a meaningful resolution (mirrors the backend gate;
    // rejects empty/whitespace/placeholder). The backend re-enforces this, so a crafted client cannot bypass it.
    if (!isMeaningfulResolution(newSolution.resolution)) {
      toast({
        title: t('sb.toast.missingInfo'),
        description: 'Enter a meaningful resolution (not empty or placeholder) before submitting to the Solution Bank.',
        variant: "destructive",
      });
      return;
    }

    // The category must come from the tenant's taxonomy. Nothing here invents one: 'all' is a filter
    // sentinel and an empty value means the author never opened the picker — both would produce a
    // record the approval gate rejects, so they are refused at the point of authoring instead.
    if (!newSolution.category || newSolution.category === 'all'
        || !tenantCategories.includes(newSolution.category)) {
      toast({
        title: t('sb.toast.missingInfo'),
        description: canAuthorCategory ? t('sb.cat.choose') : t('sb.cat.noneConfiguredHint'),
        variant: "destructive",
      });
      return;
    }

    try {
      const solutionData = {
        ...newSolution,
        tags: newSolution.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        success_rate: 0,
        usage_count: 0,
        status: 'pending_approval',  // Mark as pending for Manager approval
        submitted_by: 'operator',
        submitted_at: new Date().toISOString()
      };

      await createSolution(solutionData);

      // The solution is persisted with status 'pending_approval' and surfaces to
      // managers via the real /solutions pending-approval list (SolutionBankManager).
      // A genuine cross-user manager push notification is future backend work
      // (see WEB_CONTROL backlog: B13 notifications / Phase 6) — no fake
      // localStorage notification is written here.

      // Reset form
      setNewSolution({
        title: '',
        description: '',
        category: '',
        tags: '',
        resolution: '',
        difficulty: 'Medium'
      });
      setAddSolutionOpen(false);

      // Reload solutions
      await loadSolutions();

      // Show success toast to operator
      toast({
        title: t('sb.toast.submitted'),
        description: t('sb.toast.submittedDesc'),
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('sb.toast.addFailed'),
        variant: "destructive",
      });
    }
  };

  const handleExport = async (format) => {
    try {
      setExportOpen(true);
      toast({
        title: t('sb.toast.exportStarted'),
        description: t('sb.toast.exportStartedDesc', { format: format.toUpperCase() }),
      });
      
      const exportData = await exportSolutions(format);
      
      // Create downloadable file
      let content, mimeType, filename;
      
      if (format === 'csv') {
        content = exportData.content;
        mimeType = 'text/csv';
        filename = exportData.filename || `solutions_${Date.now()}.csv`;
      } else if (format === 'json') {
        content = JSON.stringify(exportData.content, null, 2);
        mimeType = 'application/json';
        filename = exportData.filename || `solutions_${Date.now()}.json`;
      } else {
        content = exportData.content;
        mimeType = 'application/octet-stream';
        filename = exportData.filename || `solutions_${Date.now()}.${format}`;
      }
      
      // Create and trigger download
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: t('sb.toast.exportComplete'),
        description: t('sb.toast.exportCompleteDesc', { format: format.toUpperCase() }),
      });

    } catch (error) {
      toast({
        title: t('sb.toast.exportFailed'),
        description: t('sb.toast.exportFailedDesc'),
        variant: "destructive",
      });
    } finally {
      setExportOpen(false);
    }
  };

  const handleImport = async () => {
    if (!csvFile) {
      toast({
        title: t('sb.toast.noFile'),
        description: t('sb.toast.noFileDesc'),
        variant: "destructive",
      });
      return;
    }

    try {
      setImportOpen(true);
      toast({
        title: t('sb.toast.importStarted'),
        description: t('sb.toast.importStartedDesc'),
      });
      
      const csvContent = await csvFile.text();
      const result = await importSolutions(csvContent);
      
      // Reload solutions
      await loadSolutions();
      
      toast({
        title: t('sb.toast.importComplete'),
        description: result.message || t('sb.toast.importCompleteDesc'),
      });

      setCsvFile(null);
      setImportCsvOpen(false);

    } catch (error) {
      toast({
        title: t('sb.toast.importFailed'),
        description: t('sb.toast.importFailedDesc'),
        variant: "destructive",
      });
    } finally {
      setImportOpen(false);
    }
  };

  const handleCsvFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
    } else {
      toast({
        title: t('sb.toast.invalidFile'),
        description: t('sb.toast.invalidFileDesc'),
        variant: "destructive",
      });
    }
  };

  const handleGenerateReport = async () => {
    try {
      setGeneratingReport(true);
      const report = await generatePerformanceReport();
      setReportData(report);
      setReportOpen(true);
      
      toast({
        title: t('sb.toast.reportGenerated'),
        description: t('sb.toast.reportGeneratedDesc'),
      });

    } catch (error) {
      toast({
        title: t('sb.toast.reportFailed'),
        description: t('sb.toast.reportFailedDesc'),
        variant: "destructive",
      });
    } finally {
      setGeneratingReport(false);
    }
  };

  // RESUBMIT — the author's way back into review after a reviewer returned the candidate.
  //
  // "Return for correction" put the record in `needs_details`, which no Web surface listed: not the
  // Pending tab (queries pending_approval), not Approved, not Rejected. The only Resubmit control
  // lived inside the Rejected list, so a returned solution was invisible and unrecoverable — the
  // author could correct it forever and it would never re-enter review.
  //
  // Gated on STATUS, not role. The endpoint requires an authenticated caller in the owning tenant and
  // checks nothing else, so gating this button on `role === 'operator'` would have stranded exactly
  // the Administrator who hit this. The three statuses below mirror the endpoint's own allowlist.
  const RESUBMITTABLE = ['needs_details', 'rejected', 'revoked'];
  const handleResubmit = async (solution) => {
    try {
      const r = await makeAuthenticatedRequest(`/api/v1/solutions/${solution.id}/resubmit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!r.ok) {
        let detail = `Server returned ${r.status}`;
        try { const d = await r.json(); detail = d.detail || detail; } catch { /* keep status */ }
        throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
      }
      toast({ title: t('sb.review.toast.resubmitted'), description: t('sb.review.toast.resubmittedDesc') });
      loadSolutions();
    } catch (e) {
      // Surface the server's governed reason verbatim — a returned candidate that still fails
      // validation must say why, not fail silently.
      toast({ title: t('sb.review.toast.resubmitFailed'), description: e.message, variant: 'destructive' });
    }
  };

  // REVOKE — the governed way to take live knowledge out of service before correcting it.
  //
  // The endpoint has existed since the approval lifecycle was built ("sets status to 'revoked' so AI
  // no longer uses it") but no client ever called it, so an approved solution could only be fixed by
  // editing it in place — which left it approved and being served to customers while it changed.
  // The backend now refuses that edit, and this is the control that makes the correct path reachable:
  // revoke -> edit -> resubmit -> approved by a different reviewer.
  const handleRevoke = async (solution) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('sb.revoke.confirm'))) return;
    try {
      const r = await makeAuthenticatedRequest(`/api/v1/solutions/${solution.id}/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: '' }),
      });
      if (!r.ok) {
        let detail = `Server returned ${r.status}`;
        try { const d = await r.json(); detail = d.detail || detail; } catch { /* keep status */ }
        throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
      }
      toast({ title: t('sb.revoke.done'), description: t('sb.revoke.doneDesc') });
      loadSolutions();
    } catch (e) {
      toast({ title: t('sb.revoke.failed'), description: e.message, variant: 'destructive' });
    }
  };

  // Edit solution functionality
  const handleEditSolution = (solution) => {
    setSolutionToEdit(solution);
    setNewSolution({
      title: solution.title || '',
      description: solution.description || '',
      // An existing row keeps whatever it was authored with, including a legacy value, so the editor
      // shows the truth. It is not selectable from the picker, so saving moves it onto the taxonomy.
      category: solution.category || '',
      tags: Array.isArray(solution.tags) ? solution.tags.join(', ') : '',
      resolution: solution.resolution || '',
      difficulty: solution.difficulty || 'Medium'
    });
    setEditSolutionOpen(true);
  };

  const handleUpdateSolution = async () => {
    if (!solutionToEdit || !newSolution.title || !newSolution.description) {
      toast({
        title: t('sb.toast.missingInfo'),
        description: t('sb.toast.missingInfoDesc'),
        variant: "destructive",
      });
      return;
    }

    // Same rule on edit — this is the path that moves a legacy row onto the taxonomy, so it must
    // insist on a real category rather than letting the legacy value be saved back unchanged.
    if (!newSolution.category || newSolution.category === 'all'
        || !tenantCategories.includes(newSolution.category)) {
      toast({
        title: t('sb.toast.missingInfo'),
        description: canAuthorCategory ? t('sb.cat.choose') : t('sb.cat.noneConfiguredHint'),
        variant: "destructive",
      });
      return;
    }

    try {
      const solutionData = {
        ...newSolution,
        tags: newSolution.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        success_rate: solutionToEdit.success_rate || 0,
        usage_count: solutionToEdit.usage_count || 0
      };

      await updateSolution(solutionToEdit.id, solutionData);
      
      setEditSolutionOpen(false);
      setSolutionToEdit(null);
      setNewSolution({
        title: '',
        description: '',
        category: '',
        tags: '',
        resolution: '',
        difficulty: 'Medium'
      });

      await loadSolutions();

      toast({
        title: t('sb.toast.updated'),
        description: t('sb.toast.updatedDesc'),
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('sb.toast.updateFailed'),
        variant: "destructive",
      });
    }
  };

  // Delete solution functionality
  const handleDeleteConfirm = (solution) => {
    setSolutionToDelete(solution);
    setDeleteSolutionOpen(true);
  };

  const handleDeleteSolution = async () => {
    if (!solutionToDelete) return;

    try {
      await deleteSolution(solutionToDelete.id);
      
      setDeleteSolutionOpen(false);
      setSolutionToDelete(null);

      await loadSolutions();

      toast({
        title: t('sb.toast.deleted'),
        description: t('sb.toast.deletedDesc'),
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('sb.toast.deleteFailed'),
        variant: "destructive",
      });
    }
  };

  const filteredSolutions = solutions.filter(solution => {
    const matchesSearch = solution.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         solution.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         solution.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || solution.category === selectedCategory;
    const matchesDifficulty = filterDifficulty === 'all' || solution.difficulty === filterDifficulty;
    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  // Calculate statistics
  const totalSolutions = solutions.length;
  const avgSuccessRate = totalSolutions > 0 
    ? Math.round(solutions.reduce((acc, sol) => acc + (sol.success_rate || 0), 0) / totalSolutions)
    : 0;
  const totalUsage = solutions.reduce((acc, sol) => acc + (sol.usage_count || 0), 0);
  const categoriesCount = new Set(solutions.map(sol => sol.category)).size;

  // Recent Activity — derived from the real solutions data already fetched (no hardcoded numbers).
  const isSameDay = (value) => {
    if (!value) return false;
    const dt = new Date(value);
    if (isNaN(dt.getTime())) return false;
    const now = new Date();
    return dt.getFullYear() === now.getFullYear()
      && dt.getMonth() === now.getMonth()
      && dt.getDate() === now.getDate();
  };
  const solutionsAddedToday = solutions.filter(
    sol => isSameDay(sol.created_at || sol.submitted_at || sol.last_updated)
  ).length;
  const pendingReviewCount = solutions.filter(
    sol => sol.status === 'pending_approval' || sol.status === 'pending_review'
  ).length;

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('nav.solutionBank')}</h1>
            <p className="text-gray-600">{t('page.solutionBank.subtitle')}</p>
          </div>
          <div className="flex space-x-3">
            {/* Export/Import status indicators */}
            {exportOpen && (
              <Badge className="bg-blue-100 text-blue-800 animate-pulse">
                {t('sb.exporting')}
              </Badge>
            )}
            {importOpen && (
              <Badge className="bg-green-100 text-green-800 animate-pulse">
                {t('sb.importing')}
              </Badge>
            )}
            <Button 
              className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700"
              onClick={() => setAddSolutionOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('common.addNewSolution')}
            </Button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex space-x-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder={t('sb.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex space-x-2">
            <Dialog open={filterPanelOpen} onOpenChange={setFilterPanelOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="h-4 w-4 mr-1" />
                  {t('common.filters')}
                  {filterDifficulty !== 'all' && (
                    <Badge variant="secondary" className="ml-1 text-xs px-1">{filterDifficulty}</Badge>
                  )}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-sm">
                <DialogHeader>
                  <DialogTitle>{t('sb.filterSolutions')}</DialogTitle>
                  <DialogDescription>{t('sb.filterSolutionsDesc')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">{t('sb.difficulty')}</label>
                    <Select value={filterDifficulty} onValueChange={setFilterDifficulty}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('sb.allDifficulties')}</SelectItem>
                        <SelectItem value="Easy">{t('sb.easy')}</SelectItem>
                        <SelectItem value="Medium">{t('sb.medium')}</SelectItem>
                        <SelectItem value="Hard">{t('sb.hard')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => { setFilterDifficulty('all'); setFilterPanelOpen(false); }}>
                      {t('sb.reset')}
                    </Button>
                    <Button size="sm" onClick={() => setFilterPanelOpen(false)}>
                      {t('sb.apply')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "secondary" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className={selectedCategory === category ? 'bg-purple-100 text-purple-700 border-purple-200' : ''}
              >
                {categoryLabel(category)}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Canonical Review experience: /business/solution-review redirects here with ?tab=review so the
          legacy deep link lands on the Review tab (one implementation, no duplicate mount). */}
      <Tabs defaultValue={(canReview && new URLSearchParams(window.location.search).get('tab') === 'review') ? 'review' : 'solutions'} className="space-y-6">
        <TabsList className={`grid w-full ${canReview ? 'grid-cols-7' : 'grid-cols-6'}`}>
          <TabsTrigger value="solutions">{t('sb.tab.solutions')}</TabsTrigger>
          <TabsTrigger value="knowledge-builder">
            <Sparkles className="h-4 w-4 mr-2" />
            {t('sb.tab.knowledgeBuilder')}
          </TabsTrigger>
          {/* Review queue is manager/admin-only (backend approver-only route). Operators never see it. */}
          {canReview && (
            <TabsTrigger value="review">
              <Lightbulb className="h-4 w-4 mr-2" />
              {t('sb.tab.review')}
            </TabsTrigger>
          )}
          <TabsTrigger value="analytics">{t('sb.tab.analytics')}</TabsTrigger>
          <TabsTrigger value="live-ai">
            <Activity className="h-4 w-4 mr-2" />
            {t('sb.tab.liveAi')}
          </TabsTrigger>
          <TabsTrigger value="add-solution">{t('sb.tab.addSolution')}</TabsTrigger>
          <TabsTrigger value="manage-solutions">{t('sb.tab.manageSolutions')}</TabsTrigger>
        </TabsList>

        {/* Solutions Tab */}
        <TabsContent value="solutions" className="space-y-6">
          {/* Statistics Cards */}
          <div className="grid md:grid-cols-4 gap-6">
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{t('sb.totalSolutions')}</p>
                    <p className="text-2xl font-bold text-gray-900">{totalSolutions}</p>
                  </div>
                  <Database className="h-8 w-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{t('sb.avgSuccessRate')}</p>
                    <p className="text-2xl font-bold text-gray-900">{avgSuccessRate}%</p>
                  </div>
                  <Target className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{t('sb.totalUsage')}</p>
                    <p className="text-2xl font-bold text-gray-900">{totalUsage}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{t('sb.categories')}</p>
                    <p className="text-2xl font-bold text-gray-900">{categoriesCount}</p>
                  </div>
                  <BookOpen className="h-8 w-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Solutions List */}
          <div className="grid gap-6">
            {loading ? (
              <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                <CardContent className="p-12 text-center">
                  <Loader2 className="h-16 w-16 text-purple-600 mx-auto mb-4 animate-spin" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t('sb.loadingSolutions')}</h3>
                  <p className="text-gray-500">{t('sb.loadingSolutionsSub')}</p>
                </CardContent>
              </Card>
            ) : filteredSolutions.length === 0 ? (
              <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                <CardContent className="p-12 text-center">
                  <Database className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t('sb.noSolutionsFound')}</h3>
                  <p className="text-gray-500 mb-6">{t('sb.noSolutionsHint')}</p>
                  <p className="text-sm text-gray-400">{t('sb.mgmtFunctionsHint')}</p>
                </CardContent>
              </Card>
            ) : (
              filteredSolutions.map((solution) => (
                <Card key={solution.id} className="bg-white/70 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-shadow duration-200">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-xl mb-2">{solution.title}</CardTitle>
                        <div className="flex items-center space-x-3">
                          <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                            {categoryLabel(solution.category)}
                          </Badge>
                          <Badge variant="outline" className="text-green-600 border-green-300">
                            {t('sb.successRateBadge', { rate: solution.success_rate || 0 })}
                          </Badge>
                          <Badge variant="outline">
                            {t('sb.usesBadge', { count: solution.usage_count || 0 })}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        {solution.status === 'approved' && canReview && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-amber-500 text-amber-700 hover:bg-amber-50"
                            onClick={() => handleRevoke(solution)}
                          >
                            {t('sb.revoke.action')}
                          </Button>
                        )}
                        {RESUBMITTABLE.includes(solution.status) && (
                          <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => handleResubmit(solution)}
                          >
                            {t('sb.review.resubmit')}
                          </Button>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEditSolution(solution)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteConfirm(solution)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 mb-4">{solution.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex flex-wrap gap-2">
                        {(solution.tags || []).map((tag, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      <div className="text-sm text-gray-500">
                        {t('sb.updated', { date: formatInTenantTz(solution.last_updated || solution.created_at, { dateStyle: 'medium' }) })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* AI Knowledge Builder Tab — real KB1/KB2 backend (intake → generate 50 drafts → review/approve) */}
        <TabsContent value="knowledge-builder" className="space-y-6">
          <KnowledgeBuilder />
        </TabsContent>

        {/* Review Tab — mounted only for approvers so an operator never triggers the approver-only route */}
        {canReview && (
          <TabsContent value="review" className="space-y-6">
            <SolutionBankReview />
          </TabsContent>
        )}

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('sb.topPerforming')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {solutions
                    .sort((a, b) => (b.success_rate || 0) - (a.success_rate || 0))
                    .slice(0, 5)
                    .map((solution, index) => (
                      <div key={solution.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <Badge variant="secondary" className="w-6 h-6 rounded-full flex items-center justify-center text-xs">
                            {index + 1}
                          </Badge>
                          <div>
                            <div className="font-medium">{solution.title}</div>
                            <div className="text-sm text-gray-600">{categoryLabel(solution.category)}</div>
                          </div>
                        </div>
                        <Badge className="bg-green-100 text-green-800">
                          {solution.success_rate || 0}%
                        </Badge>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('sb.mostUsed')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {solutions
                    .sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0))
                    .slice(0, 5)
                    .map((solution, index) => (
                      <div key={solution.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <Badge variant="secondary" className="w-6 h-6 rounded-full flex items-center justify-center text-xs">
                            {index + 1}
                          </Badge>
                          <div>
                            <div className="font-medium">{solution.title}</div>
                            <div className="text-sm text-gray-600">{categoryLabel(solution.category)}</div>
                          </div>
                        </div>
                        <Badge variant="outline">
                          {t('sb.usesLower', { count: solution.usage_count || 0 })}
                        </Badge>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          </div>

        </TabsContent>

        {/* Live AI Knowledge Tab — replaces the former fabricated "AI Training" tab.
            Backend truth: AI Chat/Voice read approved solutions live per customer turn; approval is
            immediate; there is no retrain job/index/cache/4-hour cycle. This panel shows only real data. */}
        <TabsContent value="live-ai" className="space-y-6">
          <LiveAiKnowledgePanel />
        </TabsContent>

        {/* Add Solution Tab */}
        <TabsContent value="add-solution" className="space-y-6">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle>{t('sb.addNewSolutionTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Button
                  className="w-full justify-start bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white border-0"
                  onClick={() => setAddSolutionOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('sb.addNewSolutionTitle')}
                </Button>

                <div className="grid md:grid-cols-2 gap-4 mt-6">
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">{t('sb.quickTips')}</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>- {t('sb.tip1')}</li>
                      <li>- {t('sb.tip2')}</li>
                      <li>- {t('sb.tip3')}</li>
                      <li>- {t('sb.tip4')}</li>
                    </ul>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">{t('sb.recentActivity')}</h4>
                    {totalSolutions === 0 ? (
                      <p className="text-sm text-gray-500">{t('sb.noRecentActivity')}</p>
                    ) : (
                      <div className="text-sm text-gray-600 space-y-2">
                        <div className="flex justify-between">
                          <span>{t('sb.solutionsAddedToday')}</span>
                          <Badge>{solutionsAddedToday}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span>{t('sb.pendingReviewLabel')}</span>
                          <Badge variant="outline">{pendingReviewCount}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span>{t('sb.successRateLabel')}</span>
                          <Badge className="bg-green-100 text-green-800">{avgSuccessRate}%</Badge>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Manage Solutions Tab */}
        <TabsContent value="manage-solutions" className="space-y-6">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle>{t('sb.tab.manageSolutions')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-w-xl">
                <h3 className="text-lg font-medium mb-4">{t('sb.bulkOperations')}</h3>
                <div className="space-y-3">
                  <Button
                    className="w-full justify-start"
                    variant="outline"
                    onClick={() => setImportCsvOpen(true)}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {t('sb.importFromCsv')}
                  </Button>
                  <Button
                    className="w-full justify-start"
                    variant="outline"
                    onClick={() => setExportAllOpen(true)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {t('sb.exportAll')}
                  </Button>
                  <Button
                    className="w-full justify-start"
                    variant="outline"
                    onClick={handleGenerateReport}
                    disabled={generatingReport}
                  >
                    {generatingReport ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileText className="h-4 w-4 mr-2" />
                    )}
                    {t('sb.generateReport')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Additional Dialogs */}
      
      {/* Import CSV Dialog */}
      <Dialog open={importCsvOpen} onOpenChange={setImportCsvOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('sb.importFromCsv')}</DialogTitle>
            <DialogDescription>
              {t('sb.importCsvDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              <input
                type="file"
                accept=".csv"
                onChange={handleCsvFileChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
              />
              <p className="text-sm text-gray-600 mt-2">
                {csvFile ? t('sb.selectedFile', { name: csvFile.name }) : t('sb.selectCsv')}
              </p>
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setImportCsvOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleImport} disabled={!csvFile}>
                {t('sb.importSolutionsBtn')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export All Dialog */}
      <Dialog open={exportAllOpen} onOpenChange={setExportAllOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('sb.exportAll')}</DialogTitle>
            <DialogDescription>
              {t('sb.exportAllDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="h-16 flex flex-col"
                onClick={() => {
                  handleExport('csv');
                  setExportAllOpen(false);
                }}
              >
                <FileText className="h-6 w-6 mb-1" />
                <span className="text-xs">{t('sb.csvFile')}</span>
              </Button>
              <Button
                variant="outline"
                className="h-16 flex flex-col"
                onClick={() => {
                  handleExport('excel');
                  setExportAllOpen(false);
                }}
              >
                <FileText className="h-6 w-6 mb-1" />
                <span className="text-xs">{t('sb.excelFile')}</span>
              </Button>
              <Button
                variant="outline"
                className="h-16 flex flex-col"
                onClick={() => {
                  handleExport('json');
                  setExportAllOpen(false);
                }}
              >
                <Database className="h-6 w-6 mb-1" />
                <span className="text-xs">{t('sb.jsonData')}</span>
              </Button>
              <Button
                variant="outline"
                className="h-16 flex flex-col"
                onClick={() => {
                  handleExport('pdf');
                  setExportAllOpen(false);
                }}
              >
                <FileText className="h-6 w-6 mb-1" />
                <span className="text-xs">{t('sb.pdfReport')}</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Performance Report Dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <BarChart3 className="h-5 w-5 mr-2" />
              {t('sb.performanceReport')}
            </DialogTitle>
            <DialogDescription>
              {t('sb.performanceReportDesc')}
            </DialogDescription>
          </DialogHeader>
          {reportData && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">{reportData.metrics?.total_solutions || 0}</div>
                    <div className="text-sm text-gray-600">{t('sb.totalSolutions')}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{reportData.metrics?.avg_success_rate?.toFixed(1) || 0}%</div>
                    <div className="text-sm text-gray-600">{t('sb.avgSuccessRate')}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{reportData.metrics?.total_usage || 0}</div>
                    <div className="text-sm text-gray-600">{t('sb.totalUsage')}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">{Object.keys(reportData.metrics?.categories || {}).length}</div>
                    <div className="text-sm text-gray-600">{t('sb.categories')}</div>
                  </CardContent>
                </Card>
              </div>
              
              <Card>
                <CardHeader>
                  <CardTitle>{t('sb.aiAnalysisReport')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose max-w-none">
                    <div className="whitespace-pre-wrap text-sm">{reportData.report}</div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end space-x-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    // Download report as text file
                    const blob = new Blob([reportData.report], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `solution_bank_report_${new Date().toISOString().split('T')[0]}.txt`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t('sb.downloadReport')}
                </Button>
                <Button onClick={() => setReportOpen(false)}>
                  {t('common.close')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Solution Dialog */}
      <Dialog open={editSolutionOpen} onOpenChange={setEditSolutionOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('sb.editSolution')}</DialogTitle>
            <DialogDescription>
              {t('sb.editSolutionDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">{t('sb.solutionTitle')}</label>
              <Input
                placeholder={t('sb.solutionTitlePlaceholder')}
                value={newSolution.title}
                onChange={(e) => setNewSolution(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('sb.descriptionLabel')}</label>
              <Textarea
                placeholder={t('sb.descriptionPlaceholder')}
                value={newSolution.description}
                onChange={(e) => setNewSolution(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('sb.category')}</label>
                <Select 
                  value={newSolution.category} 
                  onValueChange={(value) => setNewSolution(prev => ({ ...prev, category: value }))}
                  disabled={categoriesLoaded && !canAuthorCategory}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={categoriesLoaded && !canAuthorCategory
                      ? t('sb.cat.noneConfigured') : t('sb.cat.choose')} />
                  </SelectTrigger>
                  <SelectContent>
                    {/* A row authored before this tenant had a taxonomy keeps its own value here so
                        the editor shows the truth — disabled, so saving moves it onto the taxonomy. */}
                    {newSolution.category && !tenantCategories.includes(newSolution.category) && (
                      <SelectItem value={newSolution.category} disabled>
                        {newSolution.category}
                      </SelectItem>
                    )}
                    {tenantCategories.map(category => (
                      <SelectItem key={category} value={category}>
                        {categoryLabel(category)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {categoriesLoaded && !canAuthorCategory && (
                  <p className="text-xs text-amber-700 mt-1">{t('sb.cat.noneConfiguredHint')}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium">{t('sb.difficulty')}</label>
                <Select 
                  value={newSolution.difficulty} 
                  onValueChange={(value) => setNewSolution(prev => ({ ...prev, difficulty: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Easy">{t('sb.easy')}</SelectItem>
                    <SelectItem value="Medium">{t('sb.medium')}</SelectItem>
                    <SelectItem value="Hard">{t('sb.hard')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">{t('sb.tagsLabel')}</label>
              <Input
                placeholder={t('sb.tagsPlaceholder')}
                value={newSolution.tags}
                onChange={(e) => setNewSolution(prev => ({ ...prev, tags: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('sb.resolutionSteps')}</label>
              <Textarea
                placeholder={t('sb.resolutionPlaceholder')}
                value={newSolution.resolution}
                onChange={(e) => setNewSolution(prev => ({ ...prev, resolution: e.target.value }))}
                rows={4}
              />
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setEditSolutionOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleUpdateSolution}>
                {t('sb.updateSolution')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteSolutionOpen} onOpenChange={setDeleteSolutionOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('sb.deleteSolution')}</DialogTitle>
            <DialogDescription>
              {t('sb.deleteSolutionDesc')}
            </DialogDescription>
          </DialogHeader>
          {solutionToDelete && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="font-medium text-red-800 mb-1">{solutionToDelete.title}</h4>
                <p className="text-sm text-red-700">{solutionToDelete.description}</p>
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setDeleteSolutionOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteSolution}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('sb.deleteSolution')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add New Solution Dialog */}
      <Dialog open={addSolutionOpen} onOpenChange={setAddSolutionOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('sb.addNewSolutionTitle')}</DialogTitle>
            <DialogDescription>
              {t('sb.addNewSolutionDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">{t('sb.solutionTitle')}</label>
              <Input
                placeholder={t('sb.solutionTitlePlaceholder')}
                value={newSolution.title}
                onChange={(e) => setNewSolution(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('sb.descriptionLabel')}</label>
              <Textarea
                placeholder={t('sb.descriptionPlaceholder')}
                value={newSolution.description}
                onChange={(e) => setNewSolution(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('sb.category')}</label>
                <Select 
                  value={newSolution.category} 
                  onValueChange={(value) => setNewSolution(prev => ({ ...prev, category: value }))}
                  disabled={categoriesLoaded && !canAuthorCategory}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={categoriesLoaded && !canAuthorCategory
                      ? t('sb.cat.noneConfigured') : t('sb.cat.choose')} />
                  </SelectTrigger>
                  <SelectContent>
                    {/* A row authored before this tenant had a taxonomy keeps its own value here so
                        the editor shows the truth — disabled, so saving moves it onto the taxonomy. */}
                    {newSolution.category && !tenantCategories.includes(newSolution.category) && (
                      <SelectItem value={newSolution.category} disabled>
                        {newSolution.category}
                      </SelectItem>
                    )}
                    {tenantCategories.map(category => (
                      <SelectItem key={category} value={category}>
                        {categoryLabel(category)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {categoriesLoaded && !canAuthorCategory && (
                  <p className="text-xs text-amber-700 mt-1">{t('sb.cat.noneConfiguredHint')}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium">{t('sb.difficulty')}</label>
                <Select 
                  value={newSolution.difficulty} 
                  onValueChange={(value) => setNewSolution(prev => ({ ...prev, difficulty: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Easy">{t('sb.easy')}</SelectItem>
                    <SelectItem value="Medium">{t('sb.medium')}</SelectItem>
                    <SelectItem value="Hard">{t('sb.hard')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">{t('sb.tagsLabel')}</label>
              <Input
                placeholder={t('sb.tagsPlaceholder')}
                value={newSolution.tags}
                onChange={(e) => setNewSolution(prev => ({ ...prev, tags: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('sb.resolutionSteps')}</label>
              <Textarea
                placeholder={t('sb.resolutionPlaceholder')}
                value={newSolution.resolution}
                onChange={(e) => setNewSolution(prev => ({ ...prev, resolution: e.target.value }))}
                rows={4}
              />
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setAddSolutionOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleAddSolution}>
                <Plus className="h-4 w-4 mr-2" />
                {t('sb.addSolutionBtn')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SolutionBank;